"use client";

import { useState, useEffect, useMemo } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import dynamic from "next/dynamic";
import { useTheme } from "next-themes";
import { useQuery } from "@tanstack/react-query";
import { XError } from "@/utils/error.utils";
import { reportClientWarning } from "@/lib/clientReporting";
const WilsonLottie = dynamic(() => import("./WilsonLottie").then(mod => mod.WilsonLottie), {
  ssr: false
} // boolean-naming-exempt: external contract — Next.js dynamic() option
);
interface WilsonLoaderProps {
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  text?: string;
  className?: string;
  animation?: string;
}
const sizeMap = {
  xs: 16,
  sm: 32,
  md: 48,
  lg: 64,
  xl: 96
};
const lottieAnimations = ["box", "calendar", "car", "cheese", "clock", "confetti", "mail1", "mail2", "move", "paper", "toaster", "vinyl", "wave1", "wave2", "wave3"];
const animationFileMap: Record<string, string> = {
  box: "Wilson_Box.json",
  calendar: "Wilson_Calendar.json",
  car: "Wilson_Car.json",
  cheese: "Wilson_Cheese.json",
  clock: "Wilson_Clock.json",
  confetti: "Wilson_Confetti.json",
  mail1: "Wilson_Mail_v1.json",
  mail2: "Wilson_Mail_v2.json",
  move: "Wilson_Move.json",
  paper: "Wilson_Paper_Fill.json",
  toaster: "Wilson_Touster.json",
  vinyl: "Wilson_Vynyl.json",
  wave1: "Wilson_Wave_v1.json",
  wave2: "Wilson_Wave_v2.json",
  wave3: "Wilson_Wave_v3.json"
};
const getRandomAnimationKey = () => {
  const randomIndex = Math.floor(Math.random() * lottieAnimations.length);
  return lottieAnimations[randomIndex];
};
const resolveAnimationKey = (animation?: string): string => {
  if (!animation) return getRandomAnimationKey();
  if (lottieAnimations.includes(animation)) {
    return animation;
  }
  return getRandomAnimationKey();
};
export function WilsonLoader({
  size = "md",
  text,
  className,
  animation
}: WilsonLoaderProps) {
  const {
    resolvedTheme,
    forcedTheme
  } = useTheme();
  const shownTheme = forcedTheme ?? resolvedTheme;
  const animationKey = useMemo(() => resolveAnimationKey(animation), [animation]);
  const [mounted, setMounted] = useState(false);
  const dimension = sizeMap[size];
  const scaleFactor = 1.8;
  const scaledDimension = Math.round(dimension * scaleFactor);
  useEffect(() => {
    setMounted(true);
  }, []);
  const isDark = shownTheme === "dark";
  const folder = isDark ? "White_Wilson" : "Black_Wilson";
  const prefix = isDark ? "W_" : "B_";
  const filename = animationFileMap[animationKey];
  const path = `/${folder}/${prefix}${filename}`;
  const {
    data: animationData,
    error: animationError
  } = useQuery<object>({
    queryKey: ["wilsonLottie", folder, filename],
    queryFn: ({
      signal
    }) => fetch(path, {
      signal
    }).then(res => {
      if (!res.ok) {
        throw new XError({
          message: `HTTP ${res.status}`
        });
      }
      return res.json();
    }),
    enabled: !!animationKey && !!shownTheme && mounted
  });
  useEffect(() => {
    if (!animationError) return;
    reportClientWarning({
      message: "Failed to load Wilson animation",
      cause: animationError as Error,
      tags: {
        surface: "wilsonLoader"
      }
    });
  }, [animationError]);
  const visual = mounted && animationData ? <div data-visual-mask className="relative overflow-hidden" style={{
    width: dimension,
    height: dimension
  }}>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <WilsonLottie animationData={animationData} size={scaledDimension} />
                </div>
            </div> : <div data-visual-mask className="flex items-center justify-center" style={{
    width: dimension,
    height: dimension
  }} aria-hidden="true">
                <WilsonIcon size={size} className="animate-pulse opacity-60" />
            </div>;
  if (text) {
    return <div className={cn("flex flex-col items-center justify-center gap-3", className)}>
                {visual}
                <p className="text-sm text-muted-foreground animate-pulse">
                    {text}
                </p>
            </div>;
  }
  return <div className={cn("inline-flex", className)} data-sentry-component="WilsonLoader" data-sentry-source-file="WilsonLoader.tsx">{visual}</div>;
}
export function WilsonIcon({
  size = "md",
  className
}: Omit<WilsonLoaderProps, "text">) {
  const dimension = sizeMap[size];
  return <Image src="/wilson-icon.png" alt="Wilson" width={dimension} height={dimension} className={cn("dark:invert", className)} data-sentry-element="Image" data-sentry-component="WilsonIcon" data-sentry-source-file="WilsonLoader.tsx" />;
}